/**
 * Shared-calendar fetch: trigger a backend job then poll until it settles.
 *
 * Endpoint sequence:
 *   POST shared_calendars/trigger_fetch  → { fetch_uuid }
 *   GET  shared_calendars/list?fetch_uuid=…&type=… → { status, results? }
 *     (poll every 2 s until status = SUCCESS or FAILURE, max 20 attempts)
 */
import { api } from '@/api/request';

export interface SharedCalendar {
  provider_calendar_id?: string;
  name?: string;
  is_primary?: boolean;
  is_default?: boolean;
  status?: string;
}

const POLL_INTERVAL_MS = 2_000;
const MAX_POLLS = 20;

async function triggerFetch(signal?: AbortSignal): Promise<string> {
  const res = await api.post<{ fetch_uuid: string }>(
    'shared_calendars/trigger_fetch',
    undefined,
    { signal }
  );
  if (!res.fetch_uuid)
    throw new Error('shared_calendars: no fetch_uuid returned');
  return res.fetch_uuid;
}

async function pollUntilReady(
  uuid: string,
  type: string,
  signal?: AbortSignal
): Promise<SharedCalendar[]> {
  for (let i = 0; i < MAX_POLLS; i++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const res = await api.get<{
      status: string;
      results?: { calendars?: SharedCalendar[] };
    }>('shared_calendars/list', { params: { fetch_uuid: uuid, type }, signal });

    if (res.status === 'SUCCESS') return res.results?.calendars ?? [];
    if (res.status === 'FAILURE')
      throw new Error('shared_calendars: fetch job failed');

    if (i < MAX_POLLS - 1) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, POLL_INTERVAL_MS);
        signal?.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        });
      });
    }
  }
  throw new Error('shared_calendars: timed out waiting for results');
}

export async function fetchSharedCalendars(
  type: string,
  signal?: AbortSignal
): Promise<SharedCalendar[]> {
  const uuid = await triggerFetch(signal);
  return pollUntilReady(uuid, type, signal);
}
